import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ToastService } from 'src/app/core/services/toast.service';
import { DashboardService } from 'src/app/core/services/dashboard.service';
import { Router } from '@angular/router';

export interface Stat {
  title: string;
  icon: string;
  value: string;
  navigate: string;
}

// Chart data
export interface ChartType {
  chart?: any;
  plotOptions?: any;
  colors?: any;
  series?: any;
  stroke?: any;
  fill?: any;
  labels?: any;
  markers?: any;
  legend?: any;
  xaxis?: any;
  yaxis?: any;
  tooltip?: any;
  grid?: any;
  datasets?: any;
  options?: any;
  toolbar?: any;
  type?: any;
  height?: any;
  dataLabels?: any;
  sparkline?: any;
  responsive?: any;
  states?: any;
  title?: any;
  subtitle?: any;
}

export interface Transaction {
  orderid: string;
  date: string;
  billingname: string;
  total: string;
  paymentstatus: string;
}

@Component({
  selector: 'app-e-sign-dashboard',
  templateUrl: './e-sign-dashboard.component.html',
  styleUrl: './e-sign-dashboard.component.scss'
})
export class ESignDashboardComponent {


  linewithDataChart: ChartType = {
    series: [{ data: [] }],
    chart: {
      height: 380,
      type: 'line',
      zoom: {
        enabled: false
      },
      toolbar: {
        show: false
      }
    },
    colors: ['rgb(33, 128, 141)'],
    dataLabels: {
      enabled: true,
    },
    title: {
      text: 'Revenue Trend',
      align: 'center'
    },
    yaxis: {
      title: {
        text: 'Amount (in ₹ 000s)'
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -25,
      offsetX: -5
    },
    responsive: [{
      breakpoint: 600,
      options: {
        chart: {
          toolbar: {
            show: false
          }
        },
        legend: {
          show: false
        },
      }
    }]
  };

  statData: Stat[] = [
    {
      icon: 'ri-stack-line',
      title: 'Total Documents',
      value: '0',
      navigate: 'totalDoc'
    }, {
      icon: 'ri-folder-user-line',
      title: 'Active Customers',
      value: '0',
      navigate: 'users'
    }, {
      icon: 'ri-wallet-2-line',
      title: 'Monthly Revenue (MRR)',
      value: '0',
      navigate: ''
    }, {
      icon: 'ri-code-s-slash-line',
      title: 'API Calls (30d)',
      value: '0',
      navigate: ''
    }
  ];

  salesAnalytics: ChartType = {};

  transactions: Transaction[] = [
    {
      orderid: '#NZ1563',
      date: '28 Mar, 2020',
      billingname: 'Frank Dean',
      total: '$164',
      paymentstatus: 'Unpaid'
    },
    {
      orderid: '#NZ1564',
      date: '28 Mar, 2020',
      billingname: 'Eddy Torres',
      total: '$141',
      paymentstatus: 'Paid'
    },
    {
      orderid: '#NZ1565',
      date: '29 Mar, 2020',
      billingname: 'Jamison Clark',
      total: '$123',
      paymentstatus: 'Paid'
    },
    {
      orderid: '#NZ1566',
      date: '30 Mar, 2020',
      billingname: 'Jewel Buckley',
      total: '$112',
      paymentstatus: 'Paid'
    },
    {
      orderid: '#NZ1567',
      date: '31 Mar, 2020',
      billingname: 'Jeffrey Waltz',
      total: '$105',
      paymentstatus: 'Unpaid'
    },
  ];

  monthlyData: any[] = [];
  months: any[] = [];
  loading: boolean = false;
  orders: any[] = [];
  selectedMonth: any;
  selectedYear = new Date().getFullYear();

  constructor(
    public formBuilder: UntypedFormBuilder,
    private toastService: ToastService,
    private dashboardService: DashboardService,
    private router: Router) {
  }

  breadCrumbItems: Array<{}> | undefined;

  ngOnInit(): void {
    const today = new Date();
    this.selectedMonth = {
      value: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
      label: `${today.toLocaleString('default', { month: 'short' })} - ${today.getFullYear()}`
    };
    this.months = this.getPreviousSixMonthsWithLabel();
    this.getMontlyDocStatus();
    this.loadDashboardData();
    this.getAccessLogs();
  }

  loadDashboardData() {
    // Load dashboard data from API
    this.dashboardService.dashboard().subscribe((data: any) => {
      this.orders = data?.recentOrders || [];
      this.statData.forEach(element => {
        if (element.title === 'Total Documents') {
          element.value = data['totalDocuments'];
        } else if (element.title === 'Active Customers') {
          element.value = data['activeUsers'];
        } else if (element.title === 'Monthly Revenue (MRR)') {
          element.value = '₹ ' + data['mrr'];
        } else if (element.title === 'API Calls (30d)') {
          element.value = data['apiCalls'];
        }

      });

      // Process and assign the data to component properties as needed
    }, (error: any) => {
      console.error('Error loading dashboard data:', error);
    });

    this.dashboardService.getAnalyticsRevenue().subscribe((revenueData: any) => {
      this.linewithDataChart.series = [{
        data: revenueData.map((item: any) => item.revenue)
      }];

      this.linewithDataChart.xaxis = {
        categories: revenueData.map((item: any) => item.month),
        title: {
          text: 'Month'
        }
      };
    }, (error: any) => {
      console.error('Error loading revenue data:', error);
    });
  }

  getPreviousSixMonthsWithLabel() {
    const result: { value: string; label: string }[] = [];
    const currentDate = new Date();

    for (let i = 5; i >= 0; i--) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );

      const year = date.getFullYear();
      const monthIndex = date.getMonth();

      const value = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
      const label = `${date.toLocaleString('default', { month: 'short' })} - ${year}`;

      result.push({ value, label });
    }

    return result;
  }

  getMontlyDocStatus() {
    if (!this.selectedMonth) {
      return;
    }

    const formattedDate = this.selectedMonth.value;

    this.dashboardService.getMonthlyDocStatus(formattedDate).subscribe(
      (res: any[]) => {
        this.monthlyData = res || [];

        const labels = this.monthlyData.map((item: any) => item?.status);
        const counts = this.monthlyData.map((item: any) => item?.count);

        this.salesAnalytics = {
          series: counts,
          chart: {
            height: 250,
            type: 'donut'
          },
          labels,
          plotOptions: {
            pie: {
              donut: {
                size: '70%'
              }
            }
          },
          tooltip: {
            y: {
              formatter: (value: number) => `${value}`
            }
          },
          dataLabels: {
            enabled: true
          },
          legend: {
            show: false
          },
          colors: ['#5664d2', '#1cbb8c', '#eeb902']
        };
      },
      () => { }
    );
  }

  logs: any[] = [];

  getAccessLogs(): void {
    const params: any = {
      limit: 10
    };
    this.loading = true;

    this.dashboardService.getAccessLogs(params).then((res: any) => {
      this.loading = false;
      this.logs = res || [];
    },
      (err: any) => {
        this.loading = false;
        this.toastService.showToaster('Failed', err.message, 'error');
      });
  }

  navigateOnLogs() {
    this.router.navigate(['/logs'])
  }
}
